import React, { useState, useEffect, useCallback } from "react";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import MenuItem from "@mui/material/MenuItem";
import {
  Dialog,
  CircularProgress,
  MenuItem as MuiMenuItem,
  FormControl,
  IconButton,
  Box,
  useMediaQuery,
  useTheme,
  InputLabel,
  Select,
} from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import {
  format,
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  startOfYear,
  endOfYear,
  startOfDay,
  endOfDay,
  isSameDay,
} from "date-fns";

// Material Dashboard components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import MDInput from "components/MDInput";

// Layout components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import DataTable from "examples/Tables/DataTable";
import Swal from "sweetalert2";

// Custom hooks
import useTitheData from "layouts/tables/data/singleTitheTableData";

function Tithe() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isSmallMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const [paymentType, setPaymentType] = React.useState("tithe");

  const {
    columns: allColumns,
    rows,
    isLoading,
    error,
    fetchTitheData,
    handleExport,
    selectedMember,
    showMemberDetails,
    closeMemberDetails,
    MemberDetailsModal,
  } = useTitheData();

  // Payment modal states
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState({
    type: "tithe",
    amount: "",
    method: "paystack",
    reference: `TITHE-${Date.now().toString().slice(-6)}`,
    paymentDate: new Date().toISOString().split("T")[0],
    status: "active",
    email: "",
  });
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);

  // Mobile states
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  // Define which columns to show/hide based on screen size
  const columns = React.useMemo(() => {
    if (isMobile) {
      return allColumns.filter(
        (col) =>
          col.accessor === "serial" || col.accessor === "memberName" || col.accessor === "amount"
      );
    }
    return allColumns;
  }, [allColumns, isMobile]);

  const [dateRange, setDateRange] = React.useState({
    startDate: startOfDay(new Date()),
    endDate: endOfDay(new Date()),
  });

  const [dateRangeType, setDateRangeType] = React.useState("daily");

  const handleStartDateChange = (date) => {
    const newDateRange = {
      startDate: startOfDay(date),
      endDate: dateRange.endDate,
    };
    setDateRange(newDateRange);
    fetchTitheData(newDateRange.startDate, newDateRange.endDate, paymentType);
  };

  const handleEndDateChange = (date) => {
    const newDateRange = {
      startDate: dateRange.startDate,
      endDate: endOfDay(date),
    };
    setDateRange(newDateRange);
    fetchTitheData(newDateRange.startDate, newDateRange.endDate, paymentType);
  };

  const handleTodayClick = () => {
    const today = new Date();
    const newDateRange = {
      startDate: startOfDay(today),
      endDate: endOfDay(today),
    };
    setDateRange(newDateRange);
    setDateRangeType("daily");
    fetchTitheData(newDateRange.startDate, newDateRange.endDate, paymentType);
  };

  const handleDateRangeTypeChange = (event) => {
    const type = event.target.value;
    setDateRangeType(type);

    const today = new Date();
    let startDate, endDate;

    switch (type) {
      case "daily":
        startDate = startOfDay(today);
        endDate = endOfDay(today);
        break;
      case "weekly":
        startDate = startOfWeek(today);
        endDate = endOfWeek(today);
        break;
      case "monthly":
        startDate = startOfMonth(today);
        endDate = endOfMonth(today);
        break;
      case "yearly":
        startDate = startOfYear(today);
        endDate = endOfYear(today);
        break;
      case "custom":
        // Keep current dates for custom range
        return;
      default:
        startDate = startOfDay(today);
        endDate = endOfDay(today);
    }

    const newDateRange = { startDate, endDate };
    setDateRange(newDateRange);
    fetchTitheData(startDate, endDate, paymentType);
  };

  const handlePaymentTypeChange = (event) => {
    const type = event.target.value;
    setPaymentType(type);
    fetchTitheData(dateRange.startDate, dateRange.endDate, type);
  };

  const formatDisplayDate = (date) => {
    try {
      return format(date, "MMM d, yyyy");
    } catch {
      return "Invalid date";
    }
  };

  // Payment Modal Handlers
  const handleAddPayment = () => {
    setSelectedPayment({
      type: "tithe",
      amount: "",
      method: "paystack",
      reference: `TITHE-${Date.now().toString().slice(-6)}`,
      paymentDate: new Date().toISOString().split("T")[0],
      status: "active",
      email: "",
    });
    setPaymentModalOpen(true);
  };

  const handlePaystackPayment = useCallback(async () => {
    setIsProcessingPayment(true);
    try {
      const token = localStorage.getItem("authToken");
      if (!token) throw new Error("Please login to continue");

      const userData = localStorage.getItem("user");
      if (!userData) {
        throw new Error("User data not found. Please login again.");
      }

      const user = JSON.parse(userData);
      const memberId = user.member_id;

      if (!memberId) {
        throw new Error("Member ID not found. Please login again.");
      }

      const requestBody = {
        member_id: memberId,
        email: selectedPayment.email,
        amount: parseFloat(selectedPayment.amount),
        type: selectedPayment.type || "tithe",
        church_id: user.church_id || null,
      };

      console.log("Initializing Paystack payment:", requestBody);

      const response = await fetch("https://api.ofi-cia.com/api/payments/paystack/initialize", {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      });

      const responseText = await response.text();
      console.log("Paystack response:", responseText);

      if (!response.ok) {
        let errorMessage = `Payment initialization failed: ${response.status}`;
        try {
          const errorData = JSON.parse(responseText);
          errorMessage = errorData.message || errorData.error || errorMessage;
        } catch (e) {
          errorMessage = responseText || errorMessage;
        }
        throw new Error(errorMessage);
      }

      const data = JSON.parse(responseText);

      if (data.data?.authorization_url) {
        window.location.href = data.data.authorization_url;
      } else {
        throw new Error("No payment URL received from Paystack");
      }
    } catch (error) {
      console.error("Paystack payment failed:", error);
      Swal.fire({
        title: "Error!",
        text: error.message || "Failed to initialize Paystack payment",
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setIsProcessingPayment(false);
    }
  }, [selectedPayment]);

  // Update reference when modal opens
  useEffect(() => {
    if (paymentModalOpen && !selectedPayment?.reference) {
      setSelectedPayment((prev) => ({
        ...prev,
        reference: `TITHE-${Date.now().toString().slice(-6)}`,
      }));
    }
  }, [paymentModalOpen, selectedPayment]);

  // Mobile filter component
  const MobileFilters = () => (
    <MDBox
      sx={{
        display: { xs: "block", md: "none" },
        p: 2,
        backgroundColor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 1,
        mb: 2,
      }}
    >
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <MDTypography variant="h6">Filters</MDTypography>
        <IconButton onClick={() => setShowMobileFilters(false)} size="small">
          <Icon>close</Icon>
        </IconButton>
      </MDBox>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            size="small"
            value={dateRangeType}
            onChange={handleDateRangeTypeChange}
            label="Date Range"
          >
            <MenuItem value="daily">Daily</MenuItem>
            <MenuItem value="weekly">Weekly</MenuItem>
            <MenuItem value="monthly">Monthly</MenuItem>
            <MenuItem value="yearly">Yearly</MenuItem>
            <MenuItem value="custom">Custom Range</MenuItem>
          </TextField>
        </Grid>

        {dateRangeType === "custom" && (
          <>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From Date"
                  value={dateRange.startDate}
                  onChange={handleStartDateChange}
                  maxDate={dateRange.endDate}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="To Date"
                  value={dateRange.endDate}
                  onChange={handleEndDateChange}
                  minDate={dateRange.startDate}
                  renderInput={(params) => <TextField {...params} fullWidth size="small" />}
                />
              </LocalizationProvider>
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <TextField
            select
            fullWidth
            size="small"
            value={paymentType}
            onChange={handlePaymentTypeChange}
            label="Payment Type"
          >
            <MenuItem value="welfare">Welfare</MenuItem>
            <MenuItem value="tithe">Tithe</MenuItem>
            <MenuItem value="offering">Offering</MenuItem>
            <MenuItem value="donation">Donation</MenuItem>
          </TextField>
        </Grid>

        <Grid item xs={12}>
          <MDButton fullWidth variant="outlined" onClick={handleTodayClick}>
            Today
          </MDButton>
        </Grid>
      </Grid>
    </MDBox>
  );

  if (isLoading) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography>Loading Tithe...</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <DashboardNavbar />
        <MDBox pt={6} pb={3}>
          <MDTypography color="error">Error: {error}</MDTypography>
        </MDBox>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox pt={6} pb={3}>
        <Grid container spacing={6}>
          <Grid item xs={12}>
            <Card>
              <MDBox
                mx={2}
                mt={-3}
                py={3}
                px={2}
                variant="gradient"
                bgColor="info"
                borderRadius="lg"
                coloredShadow="info"
              >
                <Grid container alignItems="center" justifyContent="space-between" spacing={2}>
                  <Grid item xs={12} md={6}>
                    <MDTypography
                      variant="h6"
                      color="white"
                      sx={{
                        fontSize: { xs: "1rem", sm: "1.25rem" },
                        textAlign: { xs: "center", md: "left" },
                      }}
                    >
                      My Tithe Report - {formatDisplayDate(dateRange.startDate)}
                      {!isSameDay(dateRange.startDate, dateRange.endDate) &&
                        ` to ${formatDisplayDate(dateRange.endDate)}`}
                    </MDTypography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <MDBox
                      display="flex"
                      alignItems="center"
                      gap={1}
                      flexWrap="wrap"
                      justifyContent={{ xs: "center", md: "flex-end" }}
                    >
                      {/* Primary Actions - Always Visible */}
                      <MDButton
                        variant="gradient"
                        color="dark"
                        onClick={handleAddPayment}
                        size={isSmallMobile ? "small" : "medium"}
                        sx={{ mb: { xs: 1, md: 0 } }}
                      >
                        <Icon sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}>add</Icon>
                        {!isSmallMobile && <>&nbsp;New Payment</>}
                      </MDButton>

                      {/* Mobile Filter Toggle */}
                      <MDButton
                        variant="outlined"
                        color="white"
                        onClick={() => setShowMobileFilters(!showMobileFilters)}
                        sx={{
                          display: { xs: "flex", md: "none" },
                          mb: { xs: 1, md: 0 },
                          minWidth: "auto",
                          px: 1,
                        }}
                      >
                        <Icon>filter_list</Icon>
                      </MDButton>

                      {/* Desktop Filters */}
                      <Box
                        sx={{
                          display: { xs: "none", md: "flex" },
                          alignItems: "center",
                          gap: 1,
                          flexWrap: "wrap",
                        }}
                      >
                        <TextField
                          select
                          size="small"
                          value={dateRangeType}
                          onChange={handleDateRangeTypeChange}
                          sx={{
                            backgroundColor: "white",
                            borderRadius: 1,
                            width: 120,
                          }}
                        >
                          <MenuItem value="daily">Daily</MenuItem>
                          <MenuItem value="weekly">Weekly</MenuItem>
                          <MenuItem value="monthly">Monthly</MenuItem>
                          <MenuItem value="yearly">Yearly</MenuItem>
                          <MenuItem value="custom">Custom Range</MenuItem>
                        </TextField>

                        {dateRangeType === "custom" && (
                          <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                              label="From"
                              value={dateRange.startDate}
                              onChange={handleStartDateChange}
                              maxDate={dateRange.endDate}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  sx={{
                                    backgroundColor: "white",
                                    borderRadius: 1,
                                    width: 120,
                                  }}
                                />
                              )}
                            />
                            <DatePicker
                              label="To"
                              value={dateRange.endDate}
                              onChange={handleEndDateChange}
                              minDate={dateRange.startDate}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  size="small"
                                  sx={{
                                    backgroundColor: "white",
                                    borderRadius: 1,
                                    width: 120,
                                  }}
                                />
                              )}
                            />
                          </LocalizationProvider>
                        )}

                        <TextField
                          select
                          size="small"
                          value={paymentType}
                          onChange={handlePaymentTypeChange}
                          sx={{
                            backgroundColor: "white",
                            borderRadius: 1,
                            width: 100,
                          }}
                        >
                          <MenuItem value="welfare">Welfare</MenuItem>
                          <MenuItem value="tithe">Tithe</MenuItem>
                          <MenuItem value="offering">Offering</MenuItem>
                          <MenuItem value="donation">Donation</MenuItem>
                        </TextField>

                        <MDButton
                          variant="outlined"
                          color="white"
                          onClick={handleTodayClick}
                          size="small"
                          sx={{ minWidth: "80px" }}
                        >
                          Today
                        </MDButton>

                        {/* Export Buttons - Icon only on mobile */}
                        <MDButton
                          variant="outlined"
                          color="white"
                          onClick={() => handleExport("csv")}
                          size={isSmallMobile ? "small" : "medium"}
                          startIcon={<Icon>download</Icon>}
                        >
                          {!isSmallMobile && "CSV"}
                        </MDButton>

                        <MDButton
                          variant="outlined"
                          color="white"
                          onClick={() => handleExport("pdf")}
                          size={isSmallMobile ? "small" : "medium"}
                          startIcon={<Icon>picture_as_pdf</Icon>}
                        >
                          {!isSmallMobile && "PDF"}
                        </MDButton>

                        <MDButton
                          variant="outlined"
                          color="white"
                          onClick={() => handleExport("print")}
                          size={isSmallMobile ? "small" : "medium"}
                          startIcon={<Icon>print</Icon>}
                        >
                          {!isSmallMobile && "Print"}
                        </MDButton>
                      </Box>
                    </MDBox>
                  </Grid>
                </Grid>
              </MDBox>

              {/* Mobile Filters Dropdown */}
              {showMobileFilters && <MobileFilters />}

              <MDBox pt={3}>
                <DataTable
                  table={{
                    columns,
                    rows: rows.map((row) => ({
                      ...row,
                      originalData: row.originalData || {
                        id: row.id,
                        memberName: row.memberName,
                        type: row.type,
                        amount: row.amount,
                        method: row.method,
                        paymentDate: row.paymentDate,
                      },
                    })),
                  }}
                  isSorted={false}
                  entriesPerPage={true}
                  showTotalEntries={true}
                  noEndBorder
                  canSearch={true}
                  searchLabel="Search tithes..."
                  printFullTable
                />
              </MDBox>
            </Card>
          </Grid>
        </Grid>
      </MDBox>
      <Footer />

      {/* Member Details Modal */}
      <MemberDetailsModal
        member={selectedMember}
        open={showMemberDetails}
        onClose={closeMemberDetails}
        churchId={null}
      />

      {/* Add Payment Modal - Mobile Responsive with FormControl and Paystack Only */}
      <Dialog
        open={paymentModalOpen}
        onClose={() => setPaymentModalOpen(false)}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <MDBox p={{ xs: 2, md: 4 }}>
          <MDTypography
            variant="h5"
            gutterBottom
            sx={{ fontSize: { xs: "1.25rem", md: "1.5rem" } }}
          >
            Pay Tithe
          </MDTypography>
          <Grid container spacing={4}>
            {/* Payment Type */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Type *</InputLabel>
                <Select
                  value={selectedPayment?.type || "tithe"}
                  onChange={(e) =>
                    setSelectedPayment({
                      ...selectedPayment,
                      type: e.target.value,
                    })
                  }
                  label="Payment Type *"
                  sx={{
                    height: "56px",
                    "& .MuiSelect-select": {
                      padding: "16.5px 14px",
                    },
                  }}
                >
                  <MuiMenuItem value="tithe">Tithe</MuiMenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Amount */}
            <Grid item xs={12} md={6}>
              <MDInput
                label="Amount *"
                type="number"
                value={selectedPayment?.amount || ""}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    amount: e.target.value,
                  })
                }
                fullWidth
                required
                size={isMobile ? "small" : "medium"}
              />
            </Grid>

            {/* Payment Method - Fixed to Paystack */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Payment Method *</InputLabel>
                <Select
                  value="paystack"
                  label="Payment Method *"
                  sx={{
                    height: "56px",
                    "& .MuiSelect-select": {
                      padding: "16.5px 14px",
                    },
                  }}
                  disabled
                >
                  <MuiMenuItem value="paystack">Paystack</MuiMenuItem>
                </Select>
              </FormControl>
            </Grid>

            {/* Reference Number */}
            <Grid item xs={12} md={6}>
              <MDInput
                label="Reference Number"
                value={selectedPayment?.reference || ""}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    reference: e.target.value,
                  })
                }
                fullWidth
                InputProps={{
                  readOnly: !!selectedPayment?.reference,
                }}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>

            {/* Email field for Paystack - Always visible now */}
            <Grid item xs={12}>
              <MDInput
                label="Email *"
                type="email"
                value={selectedPayment?.email || ""}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    email: e.target.value,
                  })
                }
                fullWidth
                required
                size={isMobile ? "small" : "medium"}
              />
            </Grid>

            {/* Payment Date */}
            <Grid item xs={12} md={6}>
              <MDInput
                label="Payment Date *"
                type="date"
                value={selectedPayment?.paymentDate || new Date().toISOString().split("T")[0]}
                onChange={(e) =>
                  setSelectedPayment({
                    ...selectedPayment,
                    paymentDate: e.target.value,
                  })
                }
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
                size={isMobile ? "small" : "medium"}
              />
            </Grid>

            {/* Status */}
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={selectedPayment?.status || "active"}
                  onChange={(e) =>
                    setSelectedPayment({
                      ...selectedPayment,
                      status: e.target.value,
                    })
                  }
                  label="Status"
                  sx={{
                    height: "56px",
                    "& .MuiSelect-select": {
                      padding: "16.5px 14px",
                    },
                  }}
                >
                  <MuiMenuItem value="active">Active</MuiMenuItem>
                  <MuiMenuItem value="inactive">Inactive</MuiMenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>
          <MDBox
            display="flex"
            justifyContent="flex-end"
            mt={4}
            gap={1}
            flexDirection={{ xs: "column", sm: "row" }}
          >
            <MDButton
              variant="gradient"
              color="secondary"
              onClick={() => setPaymentModalOpen(false)}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              Cancel
            </MDButton>

            <MDButton
              variant="gradient"
              color="info"
              onClick={handlePaystackPayment}
              disabled={!selectedPayment?.amount || !selectedPayment?.email || isProcessingPayment}
              fullWidth={isMobile}
              size={isMobile ? "small" : "medium"}
            >
              {isProcessingPayment ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Processing...
                </>
              ) : (
                "Pay Via Paystack"
              )}
            </MDButton>
          </MDBox>
        </MDBox>
      </Dialog>
    </DashboardLayout>
  );
}

export default Tithe;
